import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Grid, Paper, Tabs, Tab, Typography, Stack, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  ArrowBack, Inventory2, CheckCircleOutline, Lock, PauseCircleOutlined, LocalShipping,
  AccountBalanceWallet, Speed, Warehouse as WarehouseIcon, Category, QrCode2, Bookmark,
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import inventoryCommandService from '../../services/inventoryCommandService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import {
  PageHeader, StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, StatusTone, TypePill,
  ErrorState, TableEmptyState,
} from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// ITEM 360 — a single stock item's full inventory workspace: position,
// per-warehouse stock, batches/serials, movement ledger with running
// balance, reservations and valuation. Backed by GET
// /inventory-command/item/:productId/360. Field names for the nested arrays
// aren't fully pinned in the contract, so every accessor below tries a short
// list of plausible aliases and renders an em dash rather than guessing.
// ---------------------------------------------------------------------------

const TABS = ['Overview', 'Warehouses', 'Batches', 'Serials', 'Movements', 'Reservations', 'Valuation'];

const pick = (row: any, keys: string[]): any => {
  if (!row) return undefined;
  for (const k of keys) if (row[k] !== undefined && row[k] !== null) return row[k];
  return undefined;
};

const fmtQty = (v: any): string => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-IN'));
const fmtDate = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN');
};

const movementTone = (type: any): StatusTone => {
  const t = String(type || '').toUpperCase();
  if (t === 'IN' || t.includes('RECEIPT')) return 'success';
  if (t === 'OUT' || t.includes('ISSUE') || t.includes('DISPATCH')) return 'warning';
  return 'neutral';
};

const expiryTone = (expiry: any): { tone: StatusTone; label: string } => {
  if (!expiry) return { tone: 'neutral', label: '—' };
  const d = new Date(expiry);
  if (isNaN(d.getTime())) return { tone: 'neutral', label: String(expiry) };
  const days = Math.floor((d.getTime() - Date.now()) / 86400000);
  const label = fmtDate(expiry);
  if (days < 0) return { tone: 'error', label: `${label} · expired` };
  if (days <= 30) return { tone: 'warning', label: `${label} · ${days}d left` };
  return { tone: 'success', label };
};

/** Same label/series auto-detection used across the inventory workspace so a
 * chart doesn't break if the backend names purchase-vs-consumption fields
 * differently than expected. */
const seriesFromRows = (rows: any[]): { labelKey: string; seriesKeys: string[] } => {
  const first = rows[0] || {};
  const keys = Object.keys(first);
  const labelKey = keys.find((k) => typeof first[k] === 'string') || keys[0] || 'label';
  const seriesKeys = keys.filter((k) => k !== labelKey && typeof first[k] === 'number');
  return { labelKey, seriesKeys };
};

const ItemWorkspace360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [d, setD] = useState<any>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    inventoryCommandService.item360(Number(id))
      .then(setD)
      .catch((e: any) => setError(e?.message || 'Failed to load item 360.'))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading && !d) return <Box sx={{ display: 'grid', placeItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  if (error && !d) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/inventory')} sx={{ mb: 1 }}>Back to Inventory</Button>
        <TableSurface><ErrorState message={error} onRetry={load} /></TableSurface>
      </Box>
    );
  }
  if (!d) return null;

  const p = d.profile || {};
  const pos = d.position || {};
  const reorder = d.reorder || {};
  const valuation = d.valuation || {};
  const stockByWarehouse: any[] = d.stockByWarehouse || [];
  const availability: any[] = d.availability || [];
  const batches: any[] = d.batches || [];
  const serials: any[] = d.serials || [];
  const movements: any[] = d.movements || [];
  const reservations: any[] = d.reservations || [];
  const pvc: any[] = d.purchaseVsConsumption || [];
  const pvcSeries = seriesFromRows(pvc);

  const name = pick(p, ['name', 'productName']);
  const code = pick(p, ['code', 'productCode']);
  const uom = pick(p, ['uom', 'unitOfMeasure', 'unit']);
  const materialType = pick(p, ['materialType', 'type']);
  const category = pick(p, ['category', 'categoryName']);
  const hsn = pick(p, ['hsn', 'hsnCode']);
  const gstRate = pick(p, ['gstRate']);
  const isActive = pick(p, ['isActive']);

  const subtitleBits = [
    category || null,
    uom ? `Unit ${uom}` : null,
    hsn ? `HSN ${hsn}` : null,
    gstRate != null ? `GST ${gstRate}%` : null,
  ].filter(Boolean);

  const valuationLayers: any[] | null = Array.isArray(valuation.layers) ? valuation.layers
    : Array.isArray(valuation.lots) ? valuation.lots
    : Array.isArray(valuation) ? valuation : null;
  const valuationHeader = Array.isArray(valuation) ? {} : valuation;

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/inventory')} sx={{ mb: 1 }}>Back to Inventory</Button>

      <PageHeader
        title={name || '—'}
        qualifier={code ? `· ${code}` : undefined}
        subtitle={subtitleBits.join('  ·  ')}
        actions={
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            {materialType && <TypePill label={materialType} tint={c.purple} />}
            {isActive !== undefined && <StatusDot label={isActive ? 'Active' : 'Inactive'} tone={isActive ? 'success' : 'neutral'} />}
          </Box>
        }
      />

      {/* Position KPI row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2.5 }}>
        <StatCard title="On Hand" value={pos.onHand != null ? fmtQty(pos.onHand) : null} icon={<Inventory2 />} color="#2563eb" />
        <StatCard title="Available" value={pos.available != null ? fmtQty(pos.available) : null} icon={<CheckCircleOutline />} color="#16a34a" />
        <StatCard title="Reserved" value={pos.reserved != null ? fmtQty(pos.reserved) : null} icon={<Lock />} color="#d97706" />
        <StatCard title="QC Hold" value={pos.qcHold != null ? fmtQty(pos.qcHold) : null} icon={<PauseCircleOutlined />} color="#7c3aed" />
        <StatCard title="Incoming" value={pos.incoming != null ? fmtQty(pos.incoming) : null} icon={<LocalShipping />} color="#0891b2" />
        <StatCard
          title="Stock Value"
          value={pick(valuationHeader, ['totalValue', 'value', 'stockValue']) != null ? formatIndianNumber(Number(pick(valuationHeader, ['totalValue', 'value', 'stockValue']))) : null}
          icon={<AccountBalanceWallet />}
          color="#059669"
        />
        <StatCard
          title="Reorder Level"
          value={pick(reorder, ['level', 'reorderLevel']) != null ? fmtQty(pick(reorder, ['level', 'reorderLevel'])) : null}
          icon={<Speed />}
          color="#dc2626"
        />
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        {TABS.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* Overview */}
      {tab === 0 && (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Speed fontSize="small" /> Reorder Planning
              </Typography>
              {Object.keys(reorder).length ? (
                <Stack spacing={1}>
                  {[
                    ['Reorder Level', pick(reorder, ['level', 'reorderLevel'])],
                    ['Minimum Stock', pick(reorder, ['minStock', 'minimumStock'])],
                    ['Maximum Stock', pick(reorder, ['maxStock', 'maximumStock'])],
                    ['Safety Stock', pick(reorder, ['safetyStock'])],
                    ['Reorder Quantity', pick(reorder, ['reorderQty', 'suggestedQty', 'suggestedOrderQty'])],
                    ['Lead Time', pick(reorder, ['leadTimeDays']) != null ? `${pick(reorder, ['leadTimeDays'])} days` : undefined],
                  ].map(([label, val]) => (
                    <Box key={label as string} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{val != null ? fmtQty(val) : '—'}</Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No reorder policy configured for this item.</Typography>
              )}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Purchase vs Consumption</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Trend over recent periods</Typography>
              {pvc.length ? (
                <Box sx={{ height: 260, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={pvc} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
                      <CartesianGrid {...gridProps(ct)} />
                      <XAxis dataKey={pvcSeries.labelKey} {...axisProps(ct)} />
                      <YAxis {...axisProps(ct)} width={54} tickFormatter={(v: number) => formatIndianNumber(v)} />
                      <RTooltip {...tooltipStyle(ct)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {pvcSeries.seriesKeys.map((k, i) => (
                        i === 0
                          ? <Bar key={k} dataKey={k} name={k} fill={ct.series(i)} radius={[3, 3, 0, 0]} maxBarSize={28} />
                          : <Line key={k} type="monotone" dataKey={k} name={k} stroke={ct.series(i)} strokeWidth={2} dot={false} />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <TableEmptyState title="No purchase/consumption data" hint="No purchase or consumption history for this item yet." />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Warehouses */}
      {tab === 1 && (
        <Stack spacing={2.5}>
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Warehouse', 'On Hand', 'Available', 'Reserved', 'QC Hold', 'Value'].map((h, i) => (
                      <TableCell key={h} sx={headerCellSx} align={i === 0 ? 'left' : 'right'}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockByWarehouse.map((w: any, i: number) => {
                    const wid = pick(w, ['warehouseId', 'id']);
                    return (
                      <TableRow key={wid ?? i} hover sx={{ ...(bodyRowSx as any), cursor: wid ? 'pointer' : 'default' }} onClick={() => wid && navigate(`/inventory/warehouse/${wid}`)}>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{pick(w, ['warehouse', 'warehouseName', 'name']) || '—'}</Typography></TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(pick(w, ['onHand', 'qty', 'quantity']))}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(pick(w, ['available']))}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(pick(w, ['reserved']))}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(pick(w, ['qcHold']))}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{pick(w, ['value', 'stockValue']) != null ? formatCurrency(Number(pick(w, ['value', 'stockValue']))) : '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!stockByWarehouse.length && <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No stock in any warehouse" /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>

          {!!availability.length && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <WarehouseIcon fontSize="small" /> Available-to-Promise
              </Typography>
              <TableSurface>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {Object.keys(availability[0]).map((k, i) => (
                          <TableCell key={k} sx={headerCellSx} align={i === 0 ? 'left' : 'right'}>{k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availability.map((row: any, i: number) => (
                        <TableRow key={i} hover sx={bodyRowSx as any}>
                          {Object.keys(availability[0]).map((k, j) => (
                            <TableCell key={k} align={j === 0 ? 'left' : 'right'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {row[k] === null || row[k] === undefined ? '—' : typeof row[k] === 'number' ? fmtQty(row[k]) : String(row[k])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableSurface>
            </Box>
          )}
        </Stack>
      )}

      {/* Batches */}
      {tab === 2 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Batch No', 'Warehouse', 'Qty', 'Mfg Date', 'Expiry', 'Value'].map((h, i) => (
                    <TableCell key={h} sx={headerCellSx} align={i === 2 || i === 5 ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((b: any, i: number) => {
                  const exp = expiryTone(pick(b, ['expiryDate', 'expiry', 'expDate']));
                  return (
                    <TableRow key={i} hover sx={bodyRowSx as any}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{pick(b, ['batchNo', 'batchNumber', 'batch', 'lotNo']) || '—'}</TableCell>
                      <TableCell>{pick(b, ['warehouse', 'warehouseName']) || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(pick(b, ['qty', 'quantity']))}</TableCell>
                      <TableCell>{fmtDate(pick(b, ['mfgDate', 'manufactureDate']))}</TableCell>
                      <TableCell><StatusDot label={exp.label} tone={exp.tone} /></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{pick(b, ['value', 'stockValue']) != null ? formatCurrency(Number(pick(b, ['value', 'stockValue']))) : '—'}</TableCell>
                    </TableRow>
                  );
                })}
                {!batches.length && <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No batches" hint="This item is not batch-tracked, or no batches exist." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* Serials */}
      {tab === 3 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Serial No', 'Warehouse', 'Status', 'Reference'].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {serials.map((s: any, i: number) => (
                  <TableRow key={i} hover sx={bodyRowSx as any}>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontFamily: 'monospace', fontWeight: 700 }}><QrCode2 sx={{ fontSize: 15, color: 'text.disabled' }} />{pick(s, ['serialNo', 'serialNumber', 'serial']) || '—'}</TableCell>
                    <TableCell>{pick(s, ['warehouse', 'warehouseName']) || '—'}</TableCell>
                    <TableCell><StatusDot label={pick(s, ['status', 'state']) || 'Unknown'} tone={movementTone(pick(s, ['status', 'state']))} /></TableCell>
                    <TableCell>{pick(s, ['reference', 'ref', 'soNumber']) || '—'}</TableCell>
                  </TableRow>
                ))}
                {!serials.length && <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No serials" hint="This item is not serial-tracked, or no serials exist." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* Movements */}
      {tab === 4 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Date', 'Type', 'Warehouse', 'Qty', 'Source', 'Running Balance'].map((h, i) => (
                    <TableCell key={h} sx={headerCellSx} align={i === 3 || i === 5 ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((m: any, i: number) => {
                  const type = pick(m, ['type', 'movementType']);
                  const qty = pick(m, ['qty', 'quantity']);
                  const tone = movementTone(type);
                  return (
                    <TableRow key={i} hover sx={bodyRowSx as any}>
                      <TableCell><Typography variant="caption">{fmtDate(pick(m, ['date', 'movementDate', 'ts']))}</Typography></TableCell>
                      <TableCell><StatusDot label={type || 'Unknown'} tone={tone} /></TableCell>
                      <TableCell>{pick(m, ['warehouse', 'warehouseName']) || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: tone === 'success' ? 'success.main' : tone === 'warning' ? 'warning.main' : 'inherit' }}>
                        {qty != null ? `${tone === 'success' ? '+' : tone === 'warning' ? '-' : ''}${fmtQty(qty)}` : '—'}
                      </TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pick(m, ['source', 'reference', 'ref', 'referenceType']) || '—'}</Typography></TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtQty(pick(m, ['runningBalance', 'balance']))}</TableCell>
                    </TableRow>
                  );
                })}
                {!movements.length && <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No stock movements" hint="No transactions have touched this item yet." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* Reservations */}
      {tab === 5 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Date', 'Warehouse', 'Qty', 'Demand Source', 'Status'].map((h, i) => (
                    <TableCell key={h} sx={headerCellSx} align={i === 2 ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.map((r: any, i: number) => (
                  <TableRow key={i} hover sx={bodyRowSx as any}>
                    <TableCell><Typography variant="caption">{fmtDate(pick(r, ['date', 'reservedAt', 'createdAt']))}</Typography></TableCell>
                    <TableCell>{pick(r, ['warehouse', 'warehouseName']) || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtQty(pick(r, ['qty', 'quantity', 'reservedQty']))}</TableCell>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><Bookmark sx={{ fontSize: 14, color: 'text.disabled' }} />{pick(r, ['demandSource', 'source', 'soNumber', 'reference']) || '—'}</TableCell>
                    <TableCell><StatusDot label={pick(r, ['status', 'state']) || 'Unknown'} tone={severityForReservation(pick(r, ['status', 'state']))} /></TableCell>
                  </TableRow>
                ))}
                {!reservations.length && <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><TableEmptyState title="No reservations" hint="No demand is currently reserving this item." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* Valuation */}
      {tab === 6 && (
        <Stack spacing={2.5}>
          {!!Object.keys(valuationHeader || {}).length && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Category fontSize="small" /> Valuation Summary
              </Typography>
              <Grid container spacing={2}>
                {[
                  ['Total Value', pick(valuationHeader, ['totalValue', 'value', 'stockValue']), true],
                  ['Average Cost', pick(valuationHeader, ['avgCost', 'averageCost']), true],
                  ['Valuation Method', pick(valuationHeader, ['method', 'basis']), false],
                  ['Last Purchase Cost', pick(valuationHeader, ['lastPurchaseCost', 'lastCost']), true],
                ].map(([label, val, isMoney]) => (
                  val !== undefined && (
                    <Grid size={{ xs: 6, md: 3 }} key={label as string}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{isMoney ? formatCurrency(Number(val)) : String(val)}</Typography>
                    </Grid>
                  )
                ))}
              </Grid>
            </Paper>
          )}
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(valuationLayers?.length ? Object.keys(valuationLayers[0]) : ['Warehouse', 'Qty', 'Unit Cost', 'Value']).map((h, i) => (
                      <TableCell key={h} sx={headerCellSx} align={i === 0 ? 'left' : 'right'}>{h.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {valuationLayers?.length ? valuationLayers.map((row: any, i: number) => (
                    <TableRow key={i} hover sx={bodyRowSx as any}>
                      {Object.keys(valuationLayers[0]).map((k, j) => (
                        <TableCell key={k} align={j === 0 ? 'left' : 'right'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row[k] === null || row[k] === undefined ? '—' : typeof row[k] === 'number' ? formatCurrency(row[k]) : String(row[k])}
                        </TableCell>
                      ))}
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No cost layers" hint="No FIFO/weighted-average cost breakdown available for this item." /></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Stack>
      )}
    </Box>
  );
};

const severityForReservation = (status: any): StatusTone => {
  const s = String(status || '').toLowerCase();
  if (s.includes('cancel') || s.includes('expired')) return 'neutral';
  if (s.includes('fulfil') || s.includes('complete')) return 'success';
  if (s.includes('pending') || s.includes('open')) return 'warning';
  return 'info';
};

export default ItemWorkspace360Page;
